'use client';

import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { documentsService, locauxService } from '@/lib/supabase/services';
import type { Document } from '@/lib/supabase/services';

const TYPE_COLORS: Record<string, { bg: string; text: string; icon: string }> = {
  'Juridique': { bg: 'bg-violet-50', text: 'text-violet-700', icon: '⚖️' },
  'Technique': { bg: 'bg-blue-50', text: 'text-blue-700', icon: '🔧' },
  'Médias': { bg: 'bg-amber-50', text: 'text-amber-700', icon: '📸' },
  'Administratif': { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: '📋' },
  'Commercial': { bg: 'bg-rose-50', text: 'text-rose-700', icon: '💼' },
};

const STATUT_COLORS: Record<string, string> = {
  'Validé': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'À vérifier': 'bg-amber-50 text-amber-700 border-amber-200',
  'Manquant': 'bg-red-50 text-red-700 border-red-200',
  'Reçu': 'bg-blue-50 text-blue-700 border-blue-200',
  'Expiré': 'bg-slate-50 text-slate-500 border-slate-200',
};

const TYPES = ['Juridique', 'Technique', 'Médias', 'Administratif', 'Commercial'];
const STATUTS = ['Validé', 'À vérifier', 'Manquant', 'Reçu', 'Expiré'];

interface UploadForm {
  localRef: string;
  type: string;
  statut: string;
  permissions: string[];
  selectedFile: File | null;
}

const defaultUploadForm: UploadForm = {
  localRef: '', type: 'Juridique', statut: 'À vérifier',
  permissions: ['Admin', 'Broker'], selectedFile: null,
};

export default function DocumentsContent() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterLocal, setFilterLocal] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [showUpload, setShowUpload] = useState(false);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [uploadForm, setUploadForm] = useState<UploadForm>(defaultUploadForm);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [locauxOptions, setLocauxOptions] = useState<{ ref: string; label: string }[]>([]);

  useEffect(() => {
    Promise.all([
      documentsService.getAll(),
      locauxService.getAll(),
    ]).then(([docsData, locauxData]) => {
      setDocuments(docsData);
      setLocauxOptions(locauxData.map(l => ({ ref: l.ref, label: `${l.ref} — ${l.title}` })));
      setLoading(false);
    });
  }, []);

  const filtered = documents.filter(d => {
    if (search && !d.nom.toLowerCase().includes(search.toLowerCase()) && !d.local.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType && d.type !== filterType) return false;
    if (filterStatut && d.statut !== filterStatut) return false;
    if (filterLocal && d.localRef !== filterLocal) return false;
    return true;
  });

  const stats = {
    total: documents.length,
    valides: documents.filter(d => d.statut === 'Validé').length,
    aVerifier: documents.filter(d => d.statut === 'À vérifier').length,
    manquants: documents.filter(d => d.statut === 'Manquant').length,
    expires: documents.filter(d => d.statut === 'Expiré').length,
  };

  const togglePermission = (p: string) => {
    setUploadForm(f => ({
      ...f,
      permissions: f.permissions.includes(p) ? f.permissions.filter(x => x !== p) : [...f.permissions, p],
    }));
  };

  const handleFileSelect = (file: File) => {
    setUploadForm(f => ({ ...f, selectedFile: file }));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleUpload = async () => {
    if (!uploadForm.selectedFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    if (!uploadForm.localRef) {
      toast.error('Veuillez sélectionner un local');
      return;
    }
    setUploading(true);
    const localOption = locauxOptions.find(l => l.ref === uploadForm.localRef);
    const created = await documentsService.create({
      local: localOption?.label.split(' — ')[1] || uploadForm.localRef,
      localRef: uploadForm.localRef,
      type: uploadForm.type,
      statut: uploadForm.statut,
      permissions: uploadForm.permissions,
    }, uploadForm.selectedFile);
    setUploading(false);
    if (created) {
      setDocuments(prev => [created, ...prev]);
      setShowUpload(false);
      setUploadForm(defaultUploadForm);
      toast.success(`"${uploadForm.selectedFile.name}" uploadé avec succès !`);
    } else {
      toast.error('Erreur lors de l\'upload');
    }
  };

  const handleValidate = async (doc: Document) => {
    const updated = await documentsService.update(doc.id, { statut: 'Validé' });
    if (updated) {
      setDocuments(prev => prev.map(d => d.id === doc.id ? updated : d));
      toast.success(`"${doc.nom}" validé !`);
    }
  };

  const handleDelete = async (id: string) => {
    const doc = documents.find(d => d.id === id);
    const ok = await documentsService.delete(id);
    if (ok) {
      setDocuments(prev => prev.filter(d => d.id !== id));
      toast.success(`"${doc?.nom}" supprimé`);
    } else {
      toast.error('Erreur lors de la suppression');
    }
    setDeleteConfirmId(null);
  };

  const openEdit = (doc: Document) => {
    setEditingDoc(doc);
  };

  const handleEditSave = async () => {
    if (!editingDoc) return;
    const updated = await documentsService.update(editingDoc.id, {
      statut: editingDoc.statut,
      type: editingDoc.type,
      permissions: editingDoc.permissions,
    });
    if (updated) {
      setDocuments(prev => prev.map(d => d.id === editingDoc.id ? { ...d, ...updated } : d));
      toast.success('Document mis à jour !');
    } else {
      toast.error('Erreur lors de la mise à jour');
    }
    setEditingDoc(null);
  };

  const docToDelete = documents.find(d => d.id === deleteConfirmId);

  return (
    <div className="px-6 lg:px-8 xl:px-10 py-7 max-w-screen-2xl mx-auto">

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">Gestion documentaire</h1>
          <p className="text-sm text-slate-500 mt-0.5">{loading ? 'Chargement...' : `${filtered.length} document${filtered.length !== 1 ? 's' : ''} · Classement, versioning, permissions`}</p>
        </div>
        <button onClick={() => { setUploadForm(defaultUploadForm); setShowUpload(true); }} className="btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          Uploader un document
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total, color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200' },
          { label: 'Validés', value: stats.valides, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
          { label: 'À vérifier', value: stats.aVerifier, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
          { label: 'Manquants', value: stats.manquants, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
          { label: 'Expirés', value: stats.expires, color: 'text-slate-500', bg: 'bg-slate-50 border-slate-200' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`rounded-xl border p-3 ${bg}`}>
            <p className={`text-xl font-700 ${color}`}>{value}</p>
            <p className={`text-xs font-600 ${color} mt-0.5`}>{label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-5 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-48 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" className="input-field pl-9" placeholder="Rechercher un document..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input-field w-auto min-w-36" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Tous les types</option>
          {TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <select className="input-field w-auto min-w-36" value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {STATUTS.map(s => <option key={s}>{s}</option>)}
        </select>
        <select className="input-field w-auto min-w-40" value={filterLocal} onChange={e => setFilterLocal(e.target.value)}>
          <option value="">Tous les locaux</option>
          {locauxOptions.map(l => <option key={l.ref} value={l.ref}>{l.ref}</option>)}
        </select>
        <div className="ml-auto flex bg-slate-100 rounded-lg p-1">
          {[{ mode: 'list', icon: '☰' }, { mode: 'grid', icon: '⊞' }].map(({ mode, icon }) => (
            <button key={mode} onClick={() => setViewMode(mode as 'list' | 'grid')} className={`px-3 py-1.5 text-sm rounded-md transition-all ${viewMode === mode ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
              {icon}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <svg className="w-8 h-8 animate-spin text-primary-700" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : viewMode === 'list' ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="table-th">Document</th>
                <th className="table-th">Type</th>
                <th className="table-th">Local</th>
                <th className="table-th">Statut</th>
                <th className="table-th">Version</th>
                <th className="table-th">Permissions</th>
                <th className="table-th">Date</th>
                <th className="table-th">Taille</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="py-12 text-center text-sm text-slate-400">Aucun document trouvé</td></tr>
              )}
              {filtered.map(doc => {
                const typeStyle = TYPE_COLORS[doc.type] || { bg: 'bg-slate-50', text: 'text-slate-600', icon: '📄' };
                return (
                  <tr key={doc.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="table-td">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${typeStyle.bg}`}>
                          <span className="text-sm">{typeStyle.icon}</span>
                        </div>
                        <div>
                          <p className="font-600 text-slate-900 text-sm">{doc.nom}</p>
                          <p className="text-xs text-slate-400">{doc.uploadedBy}</p>
                        </div>
                      </div>
                    </td>
                    <td className="table-td"><span className={`text-xs font-600 px-2 py-0.5 rounded-full ${typeStyle.bg} ${typeStyle.text}`}>{doc.type}</span></td>
                    <td className="table-td">
                      <p className="text-xs text-slate-700 max-w-32 truncate">{doc.local}</p>
                      <p className="text-xs text-slate-400 font-mono">{doc.localRef}</p>
                    </td>
                    <td className="table-td"><span className={`status-badge border text-xs ${STATUT_COLORS[doc.statut] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{doc.statut}</span></td>
                    <td className="table-td"><span className="text-xs font-600 text-slate-600">v{doc.version}</span></td>
                    <td className="table-td">
                      <div className="flex flex-wrap gap-1">
                        {doc.permissions?.slice(0, 2).map(p => (
                          <span key={p} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full">{p}</span>
                        ))}
                        {(doc.permissions?.length || 0) > 2 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-400 rounded-full">+{doc.permissions.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="table-td text-xs text-slate-500">{doc.date}</td>
                    <td className="table-td text-xs text-slate-500">{doc.taille}</td>
                    <td className="table-td">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {doc.statut !== 'Manquant' && (
                          <button onClick={() => toast.success(`Téléchargement de "${doc.nom}"`)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700" title="Télécharger">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0L8 8m4-4v12" /></svg>
                          </button>
                        )}
                        <button onClick={() => openEdit(doc)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700" title="Modifier">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        {doc.statut === 'À vérifier' && (
                          <button onClick={() => handleValidate(doc)} className="p-1.5 rounded-lg hover:bg-emerald-50 transition-colors text-slate-400 hover:text-emerald-600" title="Valider">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          </button>
                        )}
                        <button onClick={() => setDeleteConfirmId(doc.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-slate-400 hover:text-red-500" title="Supprimer">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(doc => {
            const typeStyle = TYPE_COLORS[doc.type] || { bg: 'bg-slate-50', text: 'text-slate-600', icon: '📄' };
            return (
              <div key={doc.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-card-hover transition-shadow group">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeStyle.bg}`}>
                    <span className="text-lg">{typeStyle.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-700 text-slate-900 text-sm truncate">{doc.nom}</p>
                    <p className="text-xs text-slate-400">{doc.localRef} · v{doc.version}</p>
                  </div>
                  <span className={`status-badge border text-xs flex-shrink-0 ${STATUT_COLORS[doc.statut] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{doc.statut}</span>
                </div>
                <p className="text-xs text-slate-500 truncate mb-3">{doc.local}</p>
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>{doc.date} · {doc.taille}</span>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {doc.statut !== 'Manquant' && (
                      <button onClick={() => toast.success(`Téléchargement de "${doc.nom}"`)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </button>
                    )}
                    <button onClick={() => openEdit(doc)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button onClick={() => setDeleteConfirmId(doc.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-slate-400 hover:text-red-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Document Modal */}
      {editingDoc && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-modal w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-700 text-slate-900">Modifier le document</h2>
              <button onClick={() => setEditingDoc(null)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide">Nom du document</label>
                <input type="text" className="input-field" value={editingDoc.nom} onChange={e => setEditingDoc({ ...editingDoc, nom: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide">Type</label>
                  <select className="input-field" value={editingDoc.type} onChange={e => setEditingDoc({ ...editingDoc, type: e.target.value as Document['type'] })}>
                    {TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide">Statut</label>
                  <select className="input-field" value={editingDoc.statut} onChange={e => setEditingDoc({ ...editingDoc, statut: e.target.value as Document['statut'] })}>
                    {STATUTS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide">Permissions d'accès</label>
                <div className="flex flex-wrap gap-2">
                  {['Admin', 'Broker', 'Propriétaire', 'Locataire', 'Visiteur'].map(p => (
                    <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" className="w-3.5 h-3.5 rounded" checked={editingDoc.permissions.includes(p)} onChange={() => setEditingDoc({ ...editingDoc, permissions: editingDoc.permissions.includes(p) ? editingDoc.permissions.filter(x => x !== p) : [...editingDoc.permissions, p] })} />
                      <span className="text-xs font-500 text-slate-700">{p}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-6 border-t border-slate-200">
              <button onClick={() => setEditingDoc(null)} className="btn-secondary">Annuler</button>
              <button onClick={handleEditSave} className="btn-primary">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmId && docToDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-modal border border-slate-200 p-6 max-w-md w-full">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </div>
              <div>
                <h3 className="text-base font-700 text-slate-900 mb-1">Supprimer ce document ?</h3>
                <p className="text-sm text-slate-500 leading-relaxed">Le document <span className="font-600 text-slate-700">"{docToDelete.nom}"</span> sera définitivement supprimé.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setDeleteConfirmId(null)} className="px-4 py-2 text-sm font-600 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">Annuler</button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="px-4 py-2 text-sm font-600 text-white bg-red-600 rounded-lg hover:bg-red-700 active:scale-95 transition-all">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-modal w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-700 text-slate-900">Uploader un document</h2>
              <button onClick={() => setShowUpload(false)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${dragActive ? 'border-primary-500 bg-primary-50' : uploadForm.selectedFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-primary-300 hover:bg-primary-50/30'}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.dwg,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                />
                {uploadForm.selectedFile ? (
                  <>
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <p className="text-sm font-600 text-emerald-700">{uploadForm.selectedFile.name}</p>
                    <p className="text-xs text-emerald-500 mt-1">{uploadForm.selectedFile.size > 1024 * 1024 ? `${(uploadForm.selectedFile.size / (1024 * 1024)).toFixed(1)} Mo` : `${Math.round(uploadForm.selectedFile.size / 1024)} Ko`}</p>
                    <button
                      onClick={e => { e.stopPropagation(); setUploadForm(f => ({ ...f, selectedFile: null })); }}
                      className="mt-2 text-xs text-slate-400 hover:text-red-500 transition-colors"
                    >
                      Changer de fichier
                    </button>
                  </>
                ) : (
                  <>
                    <svg className="w-10 h-10 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                    <p className="text-sm font-600 text-slate-600">Cliquez ou glissez-déposez un fichier</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, DWG, JPG, PNG, DOC, XLS — max 50 Mo</p>
                  </>
                )}
              </div>

              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide">Local associé <span className="text-red-500">*</span></label>
                <select className="input-field" value={uploadForm.localRef} onChange={e => setUploadForm(f => ({ ...f, localRef: e.target.value }))}>
                  <option value="">— Sélectionner un local —</option>
                  {locauxOptions.map(l => <option key={l.ref} value={l.ref}>{l.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide">Type de document</label>
                  <select className="input-field" value={uploadForm.type} onChange={e => setUploadForm(f => ({ ...f, type: e.target.value }))}>
                    {TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide">Statut initial</label>
                  <select className="input-field" value={uploadForm.statut} onChange={e => setUploadForm(f => ({ ...f, statut: e.target.value }))}>
                    {STATUTS.filter(s => s !== 'Manquant').map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide">Permissions d'accès</label>
                <div className="flex flex-wrap gap-2">
                  {['Admin', 'Broker', 'Propriétaire', 'Locataire', 'Visiteur'].map(p => (
                    <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 rounded"
                        checked={uploadForm.permissions.includes(p)}
                        onChange={() => togglePermission(p)}
                      />
                      <span className="text-xs font-500 text-slate-700">{p}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between p-6 border-t border-slate-200">
              <button onClick={() => setShowUpload(false)} className="btn-secondary">Annuler</button>
              <button onClick={handleUpload} disabled={uploading} className="btn-primary disabled:opacity-60">
                {uploading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Upload en cours...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4 4m0 0L8 8m4-4v12" />
                    </svg>
                    Uploader
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
